<template>
  <main class="profile">
    <div class="profile__top">
      <router-link to="/" class="profile__back">
        <SfIcon name="chevronLeft" :size="18" :stroke-width="2.4" />
        <span>{{ $t("notes.back") }}</span>
      </router-link>
    </div>

    <h1 class="profile__title">{{ $t("profile.title") }}</h1>

    <div class="profile__layout">
    <aside class="profile__aside">
    <!-- Account header -->
    <section class="group">
      <button type="button" class="hero" @click="showEdit = true">
        <span class="hero__avatar" aria-hidden="true">{{ initials }}</span>
        <span class="hero__main">
          <span class="hero__name">{{ displayName }}</span>
          <span v-if="userEmail" class="hero__email">{{ userEmail }}</span>
          <span class="hero__edit">{{ $t("profile.edit.title") }}</span>
        </span>
        <SfIcon name="chevron" :size="14" class="row__chevron" />
      </button>
      <div v-if="userIdShort" class="row row--static">
        <span class="row__label row__label--muted">ID · {{ userIdShort }}</span>
        <button
          type="button"
          class="row__icon-btn"
          :aria-label="$t('profile.edit.copyId')"
          :title="$t('profile.edit.copyId')"
          @click="copyId"
        >
          <SfIcon name="copy" :size="15" />
        </button>
      </div>
    </section>

    <!-- Credits + plan: iOS EditProfileView credits card. -->
    <section id="subscription" class="group">
      <div class="row row--static credits">
        <span class="credits__coin" aria-hidden="true">
          <img :src="art.gold" alt="" width="22" height="22" />
        </span>
        <span class="row__label">{{ creditsLabel }}</span>
        <button
          type="button"
          class="plan-chip"
          :class="{ 'plan-chip--premium': isPremium }"
          @click="openSubscriptionInfo"
        >
          <span>{{ isPremium ? $t("profile.subscription.active") : $t("profile.upgrade") }}</span>
          <img :src="isPremium ? art.check : art.diamond" alt="" width="20" height="20" />
        </button>
      </div>
    </section>
    <p class="group__footer">
      {{ isPremium ? $t("profile.subscription.premiumDesc") : $t("profile.subscription.freeDesc") }}
    </p>

    </aside>

    <div class="profile__content">
    <!-- Devices -->
    <h2 id="devices" class="group__header group__header--first">{{ $t("profile.devices.title") }}</h2>
    <section class="group">
      <div
        v-for="device in devices"
        :key="device.id || device.deviceID || device.deviceName"
        class="row row--static"
      >
        <span
          class="tile"
          :class="{ 'tile--android': deviceIconName(device) === 'android' }"
          aria-hidden="true"
        >
          <img v-if="deviceIconName(device) === 'android'" :src="art.androidLogo" alt="" class="tile__android" />
          <SfIcon v-else :name="deviceIconName(device)" :size="16" :stroke-width="2.2" />
        </span>
        <span class="row__body">
          <span class="row__label">{{ device.model || device.deviceName || "Device" }}</span>
          <span class="row__hint">
            {{ device.systemName }}{{ device.systemVersion ? " " + device.systemVersion : "" }}
            <template v-if="device.city || device.country">
              · {{ [device.city, device.country].filter(Boolean).join(", ") }}
            </template>
          </span>
        </span>
        <span v-if="isCurrent(device)" class="row__badge">{{ $t("profile.devices.thisDevice") }}</span>
        <span v-else class="row__value">{{ formatLastConnection(device.lastConnection) }}</span>
      </div>
      <p v-if="devices.length === 0" class="row row--static row__hint">{{ $t("profile.devices.empty") }}</p>
      <button type="button" class="row row--action" @click="openPairingQr">
        <span class="tile" style="background: #ff9500" aria-hidden="true">
          <SfIcon name="devices" :size="16" :stroke-width="2.2" />
        </span>
        <span class="row__label row__label--accent">{{ $t("profile.pairing.show") }}</span>
      </button>
    </section>
    <p class="group__footer">{{ $t("profile.devices.desc") }}</p>

    <!-- Friends -->
    <h2 id="friends" class="group__header">{{ $t("profile.friends.openTitle") }}</h2>
    <section class="group">
      <div v-for="friend in friends" :key="friend.friendID" class="row row--static">
        <span class="friend-avatar" aria-hidden="true">{{ friendInitials(friend.name) }}</span>
        <span class="row__label">{{ friend.name }}</span>
      </div>
      <p v-if="friends.length === 0" class="row row--static row__hint">{{ $t("profile.friends.empty") }}</p>
      <button type="button" class="row row--action" @click="openFriendInvite">
        <span class="tile tile--art" aria-hidden="true">
          <img :src="art.golds" alt="" />
        </span>
        <span class="row__label row__label--accent">{{ $t("profile.friends.invite") }}</span>
      </button>
    </section>

    <!-- Preferences -->
    <h2 id="appearance" class="group__header">{{ $t("profile.sections.utility") }}</h2>
    <section class="group">
      <div class="row row--static">
        <span class="tile" style="background: #32ade6" aria-hidden="true">
          <SfIcon name="theme" :size="16" :stroke-width="2.2" />
        </span>
        <span class="row__label">{{ $t("profile.theme") }}</span>
        <div class="segmented" role="radiogroup" :aria-label="$t('profile.theme')">
          <button
            v-for="opt in themeOptions"
            :key="opt.code"
            type="button"
            role="radio"
            class="segmented__btn"
            :class="{ 'segmented__btn--active': themePref === opt.code }"
            :aria-checked="themePref === opt.code"
            @click="themePref = opt.code; onThemeChange()"
          >
            {{ opt.label }}
          </button>
        </div>
      </div>
      <label class="row row--static">
        <span class="tile" style="background: #007aff" aria-hidden="true">
          <SfIcon name="language" :size="16" :stroke-width="2.2" />
        </span>
        <span class="row__label">{{ $t("profile.language") }}</span>
        <select v-model="lang" class="select" @change="onLangChange">
          <option v-for="opt in langOptions" :key="opt.code" :value="opt.code">
            {{ opt.label }}
          </option>
        </select>
      </label>
    </section>

    <!-- Support -->
    <h2 id="support" class="group__header">{{ $t("profile.sections.support") }}</h2>
    <section class="group">
      <button type="button" class="row row--action" @click="showFeedback = true">
        <span class="tile" style="background: #007aff" aria-hidden="true">
          <SfIcon name="help" :size="16" :stroke-width="2.2" />
        </span>
        <span class="row__label">{{ $t("profile.assistance") }}</span>
        <SfIcon name="chevron" :size="14" class="row__chevron" />
      </button>
      <a class="row row--action" href="mailto:contact@wellnotes.ai">
        <span class="tile" style="background: #007aff" aria-hidden="true">
          <SfIcon name="mail" :size="16" :stroke-width="2.2" />
        </span>
        <span class="row__label">{{ $t("profile.contact") }}</span>
        <span class="row__value">contact@wellnotes.ai</span>
        <SfIcon name="chevron" :size="14" class="row__chevron" />
      </a>
      <a class="row row--action" href="https://www.tiktok.com/@wellnotesapp" target="_blank" rel="noopener">
        <span class="tile tile--art" aria-hidden="true"><img :src="art.tiktok" alt="" /></span>
        <span class="row__label">TikTok</span>
        <SfIcon name="external" :size="14" class="row__chevron" />
      </a>
      <a class="row row--action" href="https://www.instagram.com/wellnotes.app" target="_blank" rel="noopener">
        <span class="tile tile--art" aria-hidden="true"><img :src="art.instagram" alt="" /></span>
        <span class="row__label">Instagram</span>
        <SfIcon name="external" :size="14" class="row__chevron" />
      </a>
    </section>

    <!-- Legal -->
    <h2 id="legal" class="group__header">{{ $t("profile.sections.legal") }}</h2>
    <section class="group">
      <a class="row row--action" href="/privacy">
        <span class="tile" style="background: #34c759" aria-hidden="true">
          <SfIcon name="shield" :size="16" :stroke-width="2.2" />
        </span>
        <span class="row__label">{{ $t("profile.legal.privacy") }}</span>
        <SfIcon name="chevron" :size="14" class="row__chevron" />
      </a>
      <a class="row row--action" href="/terms">
        <span class="tile" style="background: #34c759" aria-hidden="true">
          <SfIcon name="document" :size="16" :stroke-width="2.2" />
        </span>
        <span class="row__label">{{ $t("profile.legal.terms") }}</span>
        <SfIcon name="chevron" :size="14" class="row__chevron" />
      </a>
    </section>

    <!-- Account -->
    <h2 id="account" class="group__header">{{ $t("profile.sections.account") }}</h2>
    <section class="group">
      <button type="button" class="row row--action" @click="handleSignOut">
        <span class="tile" style="background: #8e8e93" aria-hidden="true">
          <SfIcon name="signout" :size="16" :stroke-width="2.2" />
        </span>
        <span class="row__label">{{ $t("profile.signOut") }}</span>
      </button>
      <button type="button" class="row row--action" @click="confirmDelete">
        <span class="tile" style="background: #ff3b30" aria-hidden="true">
          <SfIcon name="trash" :size="16" :stroke-width="2.2" />
        </span>
        <span class="row__label row__label--danger">{{ $t("profile.deleteAccount") }}</span>
      </button>
    </section>
    <p class="group__footer">{{ $t("profile.account.deleteHint") }}</p>
    </div>
    </div>

    <p class="profile__version">Wellnotes · Web</p>

    <EditProfileSheet :open="showEdit" @close="showEdit = false" />
    <FeedbackChat :open="showFeedback" @close="showFeedback = false" />

    <!-- Pairing QR modal -->
    <Teleport to="body">
      <div
        v-if="showPairingQr"
        class="profile-modal__backdrop"
        role="dialog"
        aria-modal="true"
        @click.self="closePairingQr"
      >
        <div class="profile-modal">
          <header class="profile-modal__header">
            <h2 class="profile-modal__title">{{ $t("profile.pairing.title") }}</h2>
            <button
              type="button"
              class="profile-modal__close"
              :aria-label="$t('common.dismiss')"
              @click="closePairingQr"
            ><SfIcon name="close" :size="16" /></button>
          </header>
          <div class="profile-modal__body profile-modal__body--qr">
            <p class="profile-modal__hint">{{ $t("profile.pairing.hint") }}</p>
            <p v-if="pairingNotice" class="profile-modal__notice" role="status">{{ pairingNotice }}</p>
            <div class="profile-modal__qr">
              <div v-if="pairingQrBusy" class="profile-modal__qr-spinner" aria-hidden="true"></div>
              <img
                v-else-if="pairingQrDataUrl"
                :src="pairingQrDataUrl"
                :alt="$t('profile.pairing.title')"
              />
              <img
                v-if="pairingQrDataUrl"
                :src="iconImg"
                alt=""
                class="signin__qr-logo"
                aria-hidden="true"
              />
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Friend invite modal -->
    <Teleport to="body">
      <div
        v-if="showFriendInvite"
        class="profile-modal__backdrop"
        role="dialog"
        aria-modal="true"
        @click.self="closeFriendInvite"
      >
        <div class="profile-modal">
          <header class="profile-modal__header">
            <h2 class="profile-modal__title">{{ $t("profile.friends.invite") }}</h2>
            <button
              type="button"
              class="profile-modal__close"
              :aria-label="$t('common.dismiss')"
              @click="closeFriendInvite"
            ><SfIcon name="close" :size="16" /></button>
          </header>
          <div class="profile-modal__body">
            <p class="profile-modal__hint">{{ $t("profile.friends.inviteHint") }}</p>
            <div v-if="friendInviteBusy" class="profile-modal__qr-spinner" aria-hidden="true"></div>
            <template v-else>
              <div class="profile-modal__link">
                <span class="profile-modal__link-url" :title="friendInviteUrl">
                  {{ friendInviteUrl }}
                </span>
              </div>
              <div class="profile-modal__link-actions">
                <button type="button" class="btn btn--secondary" @click="copyInviteLink">
                  {{ $t("profile.friends.inviteCopy") }}
                </button>
                <button type="button" class="btn btn--primary" @click="shareInviteLink">
                  {{ $t("profile.friends.inviteShare") }}
                </button>
              </div>
            </template>
          </div>
        </div>
      </div>
    </Teleport>

    <div
      v-if="showDeleteConfirm"
      class="confirm-backdrop"
      @click.self="showDeleteConfirm = false"
    >
      <div class="confirm-dialog card">
        <h3>{{ $t("profile.delete.title") }}</h3>
        <p>{{ $t("profile.delete.message") }}</p>
        <div class="confirm-actions">
          <button type="button" class="btn btn--ghost" :disabled="isDeleting" @click="showDeleteConfirm = false">
            {{ $t("profile.delete.cancel") }}
          </button>
          <button type="button" class="btn btn--danger" :disabled="isDeleting" @click="doDelete">
            <span v-if="isDeleting" class="spinner" aria-hidden="true"></span>
            {{ isDeleting ? $t("profile.delete.deleting") : $t("profile.delete.confirm") }}
          </button>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import QRCode from "qrcode";
import { httpsCallable } from "firebase/functions";
import { authState, signOut } from "../stores/authStore";
import { showSnack } from "../stores/snackbarStore";
import { auth, db, functions } from "../firebase";
import {
  deviceInfos,
  deviceLinkID,
  generatePairingSecret,
  pairingSecretHash,
  uuidV4,
} from "../utils/pairing";
import { doc, setDoc, deleteDoc, onSnapshot } from "firebase/firestore";
import { signInWithCustomToken } from "firebase/auth";
import { callable, linkErrorReason, linkReasonKey, markSeatPending } from "../utils/linking";
import { deviceIconName } from "../utils/deviceIcon";
import { setLanguage } from "../i18n";
import EditProfileSheet from "../components/EditProfileSheet.vue";
import FeedbackChat from "../components/FeedbackChat.vue";
import SfIcon from "../components/SfIcon.vue";
import iconImg from "../assets/logo.png";
import { ART } from "../assets";

const DELETE_USER_DATA_URL = "https://us-central1-revisio-ai.cloudfunctions.net/deleteUserData";

const LANG_OPTIONS = [
  { code: "en", label: "English" },
  { code: "fr", label: "Français" },
  { code: "es", label: "Español" },
  { code: "de", label: "Deutsch" },
  { code: "it", label: "Italiano" },
  { code: "pt", label: "Português" },
  { code: "nl", label: "Nederlands" },
  { code: "sv", label: "Svenska" },
  { code: "pl", label: "Polski" },
  { code: "cs", label: "Čeština" },
  { code: "ro", label: "Română" },
  { code: "el", label: "Ελληνικά" },
  { code: "tr", label: "Türkçe" },
  { code: "ru", label: "Русский" },
  { code: "uk", label: "Українська" },
  { code: "ar", label: "العربية" },
  { code: "ja", label: "日本語" },
  { code: "hi", label: "हिन्दी" },
];

export default {
  name: "ProfilePage",
  components: { EditProfileSheet, FeedbackChat, SfIcon },
  data() {
    return {
      showEdit: false,
      showFeedback: false,
      showDeleteConfirm: false,
      isDeleting: false,
      iconImg,
      art: ART,
      themePref: "auto",
      lang: (this.$i18n && this.$i18n.locale) || "en",
      langOptions: LANG_OPTIONS,
      themeOptions: [
        { code: "auto", label: this.$t("profile.themeAuto") },
        { code: "light", label: this.$t("profile.themeLight") },
        { code: "dark", label: this.$t("profile.themeDark") },
      ],
      // Pairing QR modal state — mirrors iOS DeviceView: each session uses
      // a fresh random UUID for `devices/{id}`. We listen for deletion of
      // that doc (the cloud function deletes it on successful pairing) so
      // we can immediately regenerate a fresh QR for the next device.
      showPairingQr: false,
      pairingQrDataUrl: "",
      pairingQrBusy: false,
      pairingSessionID: "",
      pairingListenerUnsub: null,
      pairingListenerSeenDoc: false,
      pairingSecret: "",
      pairingNotice: "",
      pairingRefreshTimer: null,
      followingMerge: false,
      // Friend invite modal state
      showFriendInvite: false,
      friendInviteUrl: "",
      friendInviteBusy: false,
    };
  },
  computed: {
    user() {
      return authState.userData;
    },
    isPremium() {
      return !!(this.user && this.user.premium);
    },
    // iOS: "∞ credits" for premium, otherwise the balance.
    creditsLabel() {
      if (this.isPremium) return this.$t("profile.creditsMany", { n: "∞" });
      const n = Number((this.user && this.user.credits) || 0);
      return this.$t(n > 1 ? "profile.creditsMany" : "profile.creditsOne", { n });
    },
    displayName() {
      const u = this.user;
      if (u && u.name && u.name.length > 0) return u.name;
      const cu = authState.currentUser;
      if (cu && cu.email) return cu.email.split("@")[0];
      return this.$t("profile.noName");
    },
    userEmail() {
      const cu = authState.currentUser;
      return cu && cu.email ? cu.email : "";
    },
    initials() {
      const n = this.displayName || "";
      if (!n) return "?";
      return (
        n
          .split(/[\s.@_-]+/)
          .filter(Boolean)
          .slice(0, 2)
          .map((p) => p.charAt(0).toUpperCase())
          .join("") || "?"
      );
    },
    userIdShort() {
      const id = authState.currentUser ? authState.currentUser.uid : "";
      return id ? id.slice(0, 12) + "…" : "";
    },
    devices() {
      return (this.user && this.user.devices) || [];
    },
    friends() {
      return (this.user && this.user.friends) || [];
    },
  },
  mounted() {
    try {
      const stored = localStorage.getItem("themePref");
      if (stored === "light" || stored === "dark" || stored === "auto") {
        this.themePref = stored;
      }
    } catch (_) { /* ignore */ }
    this.applyTheme();
    this.lang = (this.$i18n && this.$i18n.locale) || "en";
  },
  beforeUnmount() {
    // Clean up the listener / pairing doc if the user leaves the page
    // mid-session (best effort).
    this.stopPairingListener();
    this.cleanupPairingSession();
  },
  methods: {
    /**
     * Opens a modal showing a QR code other devices (iOS / Android / Web)
     * can scan to pair to this user's account. Mirrors the iOS pairing
     * flow in DeviceView.swift: each modal session creates a *fresh*
     * `devices/{randomUUID}` doc (instead of reusing this device's
     * persistent ID), then listens for deletion to know when pairing
     * succeeded and to regenerate a new QR for chaining additional
     * devices.
     */
    async openPairingQr() {
      if (!authState.currentUser) return;
      this.showPairingQr = true;
      this.pairingQrBusy = true;
      this.pairingQrDataUrl = "";
      // Drop any stale session before starting a new one — opening the
      // sheet a second time without this would otherwise hit Firestore's
      // rules (the previous pairing doc was either consumed or owned by
      // a different state).
      await this.cleanupPairingSession();
      try {
        await this.startPairingSession();
      } catch (err) {
        console.error("openPairingQr failed:", err);
        showSnack(this.$t("profile.pairing.error"), { variant: "error" });
        this.showPairingQr = false;
      } finally {
        this.pairingQrBusy = false;
      }
    },
    async startPairingSession() {
      const uid = authState.currentUser && authState.currentUser.uid;
      if (!uid) return;
      // A UUID: the server refuses any other pairing id.
      const id = uuidV4();
      const secret = generatePairingSecret();
      this.pairingSessionID = id;
      this.pairingSecret = secret;
      this.pairingListenerSeenDoc = false;
      const hash = await pairingSecretHash(secret);
      // Plain `setDoc` — no merge — because each session uses a fresh ID
      // and Firestore rules expect a clean create scoped to this user.
      await setDoc(doc(db, "devices", id), {
        userID: uid,
        pairingSecretHash: hash,
      });
      const payload = {
        userID: uid,
        hasAccount: "1",
        pairingID: id,
        pairingSecret: secret,
        name: this.user && this.user.name ? this.user.name : "",
        device: await deviceInfos(id),
      };
      const url = `https://wellnotes.ai/device/${JSON.stringify(payload)}`;
      this.pairingQrDataUrl = await QRCode.toDataURL(url, {
        errorCorrectionLevel: "H",
        margin: 1,
        width: 560,
        color: { dark: "#0f172a", light: "#ffffff" },
      });
      this.startPairingListener(id);
      // A signed-in device's code expires on the server after 10 minutes
      // (functions/identity/linking/policy.js): replace it a little before.
      this.clearPairingRefresh();
      this.pairingRefreshTimer = setTimeout(() => {
        if (this.pairingSessionID !== id || !this.showPairingQr) return;
        this.cleanupPairingSession().then(() => this.startPairingSession()).catch((err) => {
          console.warn("refresh pairing QR failed:", err);
        });
      }, 8 * 60 * 1000);
    },
    clearPairingRefresh() {
      if (this.pairingRefreshTimer) {
        clearTimeout(this.pairingRefreshTimer);
        this.pairingRefreshTimer = null;
      }
    },
    startPairingListener(pairingID) {
      this.stopPairingListener();
      const ref = doc(db, "devices", pairingID);
      this.pairingListenerUnsub = onSnapshot(
        ref,
        (snap) => {
          if (snap.metadata && snap.metadata.hasPendingWrites) return;
          if (snap.exists()) {
            this.pairingListenerSeenDoc = true;
            this.handlePairingUpdate(pairingID, snap.data() || {});
            return;
          }
          // Doc was deleted — the other device joined this account with
          // it. Spin up a fresh QR for the next device (unless this browser
          // is itself moving to the account that absorbed it).
          if (this.pairingListenerSeenDoc && this.showPairingQr && !this.followingMerge) {
            this.startPairingSession().catch((err) => {
              console.warn("regenerate pairing QR failed:", err);
            });
          }
        },
        (err) => {
          console.warn("pairing listener error:", err);
        },
      );
    },
    /** What the server wrote on the code after a scan. */
    handlePairingUpdate(pairingID, data) {
      if (data.status === "merged" && data.mergedInto) {
        const uid = authState.currentUser && authState.currentUser.uid;
        if (data.mergedInto === uid) {
          // This account absorbed the other one: the code is used up.
          this.pairingNotice = this.$t("linking.accountsMerged");
          deleteDoc(doc(db, "devices", pairingID)).catch(() => {});
        } else {
          this.followMergedCode(pairingID);
        }
        return;
      }
      if (data.reason === "premium-required") {
        this.pairingNotice = this.$t(linkReasonKey(data.reason));
      }
    },
    /**
     * This account was absorbed by the one that scanned the code: the
     * server left a link to the survivor on the code, redeemed with its
     * secret. (If that fails, the auth store's redirect catches up.)
     */
    async followMergedCode(pairingID) {
      if (this.followingMerge || !this.pairingSecret) return;
      this.followingMerge = true;
      try {
        const result = await callable("consumeDeviceCustomToken")({
          deviceID: pairingID,
          pairingSecret: this.pairingSecret,
        });
        const data = (result && result.data) || {};
        // Already moved there by the auth store's redirect: nothing to do.
        const alreadyThere = auth.currentUser && data.uid === auth.currentUser.uid;
        if (data.customToken && !alreadyThere) {
          markSeatPending("merge");
          await signInWithCustomToken(auth, data.customToken);
        }
        if (data.customToken) {
          showSnack(this.$t("linking.accountsMerged"));
          this.pairingNotice = "";
        }
      } catch (err) {
        console.warn("follow merged code failed:", linkErrorReason(err) || err);
      } finally {
        this.followingMerge = false;
      }
      // The used code must not stay up (by now the redirect has usually
      // moved this browser, so the new one is the survivor's).
      if (this.showPairingQr) {
        setTimeout(() => {
          if (this.showPairingQr && !this.followingMerge) {
            this.startPairingSession().catch((e) => console.warn("regenerate pairing QR failed:", e));
          }
        }, 1500);
      }
    },
    stopPairingListener() {
      if (this.pairingListenerUnsub) {
        try { this.pairingListenerUnsub(); } catch (_) { /* ignore */ }
        this.pairingListenerUnsub = null;
      }
    },
    /**
     * Best-effort delete of the previous session's pairing doc when the
     * sheet closes (or is reopened). Mirrors iOS
     * `cleanupPairingRequestIfAuthenticated` — never throws on failure
     * because the doc may already have been consumed by the linker.
     */
    async cleanupPairingSession() {
      this.stopPairingListener();
      this.clearPairingRefresh();
      this.pairingNotice = "";
      const id = this.pairingSessionID;
      if (!id) return;
      this.pairingSessionID = "";
      try {
        await deleteDoc(doc(db, "devices", id));
      } catch (_) {
        // Already deleted by the cloud function or by Firestore rules —
        // either way, nothing the user needs to know about.
      }
    },
    closePairingQr() {
      this.showPairingQr = false;
      this.pairingQrDataUrl = "";
      // Fire-and-forget — don't block the close on the network round-trip.
      this.cleanupPairingSession();
    },
    /**
     * Opens a modal with a shareable invite link for the current user.
     * Reuses `userData.inviteCode` if Firestore already has one; otherwise
     * calls the `createInviteCode` cloud function (same callable iOS uses).
     */
    async openFriendInvite() {
      this.showFriendInvite = true;
      this.friendInviteBusy = true;
      try {
        let code = (this.user && this.user.inviteCode) || "";
        if (!code) {
          const callable = httpsCallable(functions, "createInviteCode");
          const result = await callable({});
          code = (result && result.data && (result.data.code || result.data.inviteCode)) || "";
        }
        if (!code) {
          throw new Error("no-invite-code");
        }
        this.friendInviteUrl = `https://wellnotes.ai/invite/${code}`;
      } catch (err) {
        console.error("openFriendInvite failed:", err);
        showSnack(this.$t("profile.friends.inviteError"), { variant: "error" });
        this.showFriendInvite = false;
      } finally {
        this.friendInviteBusy = false;
      }
    },
    closeFriendInvite() {
      this.showFriendInvite = false;
    },
    async copyInviteLink() {
      const url = this.friendInviteUrl;
      if (!url) return;
      try {
        await navigator.clipboard.writeText(url);
        showSnack(this.$t("profile.friends.inviteCopied"), { variant: "success" });
      } catch (_) {
        showSnack(this.$t("profile.friends.inviteCopyFailed"), { variant: "error" });
      }
    },
    async shareInviteLink() {
      const url = this.friendInviteUrl;
      if (!url) return;
      // Web Share API on supported browsers (mobile mostly), fall back to copy.
      if (navigator.share) {
        try {
          await navigator.share({
            title: this.$t("profile.friends.inviteShareTitle"),
            text: this.$t("profile.friends.inviteShareText"),
            url,
          });
          return;
        } catch (err) {
          if (err && err.name === "AbortError") return;
          // fallthrough to copy
        }
      }
      this.copyInviteLink();
    },
    // DeviceView.swift `iconName(for:)`: a browser, a Mac, a Windows PC,
    // an iPad, an Android phone, else an iPhone.
    deviceIconName,
    isCurrent(d) {
      try {
        return (d.deviceID || "") === deviceLinkID();
      } catch (_) {
        return false;
      }
    },
    friendInitials(name) {
      if (!name) return "?";
      return name
        .split(" ")
        .map((p) => p.charAt(0))
        .join("")
        .slice(0, 2)
        .toUpperCase();
    },
    formatLastConnection(ts) {
      if (!ts) return "";
      try {
        const d = new Date(ts);
        if (isNaN(d.getTime())) return "";
        const diff = Date.now() - d.getTime();
        const min = Math.round(diff / 60000);
        if (min < 10) return this.$t("profile.devices.online");
        if (min < 60) return this.$t("profile.devices.minutesAgo", { n: min });
        const hr = Math.round(diff / 3600000);
        if (hr < 24) return this.$t("profile.devices.hoursAgo", { n: hr });
        const day = Math.round(diff / 86400000);
        if (day < 7) return this.$t("profile.devices.daysAgo", { n: day });
        return new Intl.DateTimeFormat(this.lang, { dateStyle: "medium" }).format(d);
      } catch (_) {
        return "";
      }
    },
    onThemeChange() {
      try { localStorage.setItem("themePref", this.themePref); } catch (_) { /* ignore */ }
      this.applyTheme();
    },
    applyTheme() {
      const root = document.documentElement;
      if (this.themePref === "auto") {
        // Match the system preference instead of removing data-theme — App.vue's
        // CSS variables are scoped to [data-theme="light|dark"], so leaving the
        // attribute unset would un-style the whole app.
        const dark = window.matchMedia
          && window.matchMedia("(prefers-color-scheme: dark)").matches;
        root.setAttribute("data-theme", dark ? "dark" : "light");
      } else {
        root.setAttribute("data-theme", this.themePref);
      }
      try { root.style.colorScheme = root.getAttribute("data-theme") || "dark"; } catch (_) { /* ignore */ }
    },
    onLangChange() {
      setLanguage(this.lang);
      this.themeOptions = [
        { code: "auto", label: this.$t("profile.themeAuto") },
        { code: "light", label: this.$t("profile.themeLight") },
        { code: "dark", label: this.$t("profile.themeDark") },
      ];
    },
    openSubscriptionInfo() {
      showSnack(this.$t("profile.subscription.iosOnly"), { variant: "info" });
    },
    async copyId() {
      const id = authState.currentUser ? authState.currentUser.uid : "";
      if (!id) return;
      try {
        await navigator.clipboard.writeText(id);
        showSnack(this.$t("profile.edit.idCopied"), { variant: "success" });
      } catch (err) {
        console.warn("copy id failed", err);
        showSnack(this.$t("profile.edit.idCopyFailed"), { variant: "error" });
      }
    },
    async handleSignOut() {
      try {
        await signOut();
      } catch (err) {
        console.error("signOut failed", err);
      } finally {
        this.$router.replace("/");
      }
    },
    confirmDelete() {
      this.showDeleteConfirm = true;
    },
    async doDelete() {
      const cu = authState.currentUser;
      if (!cu) return;
      this.isDeleting = true;
      try {
        const token = await cu.getIdToken();
        const deviceID = deviceLinkID();
        const res = await fetch(DELETE_USER_DATA_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ deviceID, uid: cu.uid }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || "delete failed");
        }
        showSnack(this.$t("profile.delete.success"), { variant: "success" });
        await signOut().catch(() => null);
        this.$router.replace("/");
      } catch (err) {
        console.error("delete account failed", err);
        showSnack(this.$t("profile.delete.failed"), { variant: "error" });
      } finally {
        this.isDeleting = false;
        this.showDeleteConfirm = false;
      }
    },
  },
};
</script>

<style scoped>
/* iOS Settings: an inset-grouped column on the grouped page colour. */
.profile {
  width: 100%;
  max-width: 1040px;
  margin: 20px auto 96px;
  padding: 0 16px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  color: var(--wn-label);
}

.profile__top {
  display: flex;
  align-items: center;
}

.profile__back {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 40px;
  padding: 0 14px 0 8px;
  margin-left: -8px;
  border-radius: 999px;
  color: var(--wn-accent-text);
  font-size: 16px;
  font-weight: 500;
  text-decoration: none;
}

.profile__back:hover {
  background: var(--wn-fill);
}

.profile__title {
  margin: 10px 0 18px;
  font-size: 34px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.1;
}

/* Wide screens: identity and plan stay pinned on the left, settings
   scroll on the right. Narrow screens stack them. */
.profile__layout {
  display: grid;
  grid-template-columns: 320px minmax(0, 1fr);
  gap: 32px;
  align-items: start;
}

.profile__aside {
  position: sticky;
  top: 20px;
}

.profile__content > .group__header--first {
  margin-top: 0;
}

@media (max-width: 860px) {
  .profile__layout {
    grid-template-columns: 1fr;
    gap: 0;
  }

  .profile__aside {
    position: static;
  }

  .profile__content > .group__header--first {
    margin-top: 28px;
  }
}

.group {
  background: var(--wn-card);
  border-radius: var(--wn-radius);
  overflow: hidden;
}

.group + .group {
  margin-top: 28px;
}

.group__header {
  margin: 28px 16px 8px;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--wn-secondary);
}

.group__footer {
  margin: 8px 16px 0;
  font-size: 13px;
  line-height: 1.4;
  color: var(--wn-secondary);
}

.group__footer + .group {
  margin-top: 28px;
}

/* --- Rows --- */
.row {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 48px;
  margin: 0;
  padding: 8px 16px;
  box-sizing: border-box;
  border: 0;
  background: transparent;
  color: var(--wn-label);
  font: inherit;
  font-size: 16px;
  text-align: left;
  text-decoration: none;
}

/* Hairline inset past the tile, as on iOS. */
.row + .row::before,
.hero + .row::before {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  left: 56px;
  height: 1px;
  background: var(--wn-separator);
  transform: scaleY(0.5);
  transform-origin: top;
}

.row--action {
  cursor: pointer;
}

.row--action:hover {
  background: var(--wn-card-hover);
}

.row--action:focus-visible,
.hero:focus-visible {
  outline: 2px solid var(--wn-accent);
  outline-offset: -2px;
}

.row__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.row__label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.row__body .row__label {
  flex: none;
}

.row__label--muted {
  color: var(--wn-secondary);
  font-size: 14px;
  font-variant-numeric: tabular-nums;
}

.row__label--accent {
  color: var(--wn-accent-text);
}

.row__label--danger {
  color: #ff3b30;
}

.row__hint {
  font-size: 13px;
  color: var(--wn-secondary);
  line-height: 1.35;
}

p.row__hint {
  padding: 14px 16px;
}

.row__value {
  flex-shrink: 0;
  font-size: 15px;
  color: var(--wn-secondary);
  font-variant-numeric: tabular-nums;
}

.row__badge {
  flex-shrink: 0;
  padding: 2px 9px;
  border-radius: 999px;
  background: var(--wn-accent-soft);
  color: var(--wn-accent-text);
  font-size: 12px;
  font-weight: 600;
}

.row__chevron {
  flex-shrink: 0;
  color: var(--wn-tertiary);
}

.row__icon-btn {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--wn-accent-text);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.row__icon-btn:hover {
  background: var(--wn-fill);
}

/* --- Tiles: the coloured 28px squares of iOS Settings --- */
.tile {
  width: 28px;
  height: 28px;
  border-radius: 7px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #007aff;
  color: #ffffff;
}

.tile--android {
  background: #3db54a;
}

.tile__android {
  width: 18px;
  height: 18px;
  object-fit: contain;
  filter: brightness(0) invert(1);
}

/* Brand and 3D illustrations fill the tile themselves. */
.tile--art {
  background: transparent;
}

.tile--art img {
  width: 28px;
  height: 28px;
  object-fit: contain;
  border-radius: 7px;
}

/* --- Account header --- */
.hero {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 14px 16px;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.hero:hover {
  background: var(--wn-card-hover);
}

.hero__avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--wn-fill);
  color: var(--wn-label);
  font-size: 22px;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.hero__main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.hero__name {
  font-size: 21px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.hero__email,
.hero__edit {
  font-size: 13px;
  color: var(--wn-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.hero__edit {
  color: var(--wn-accent-text);
  font-weight: 500;
}

/* --- Credits card (iOS EditProfileView) --- */
.credits__coin {
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  border: 2px solid transparent;
  background:
    linear-gradient(var(--wn-card), var(--wn-card)) padding-box,
    linear-gradient(135deg, #21b4f3, #ff44f9, #ffcc00) border-box;
}

.credits__coin img {
  width: 22px;
  height: 22px;
  object-fit: contain;
}

.credits .row__label {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.plan-chip {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 12px;
  border: 0;
  border-radius: 8px;
  background: rgba(255, 204, 0, 0.1);
  color: var(--wn-accent-text);
  font: inherit;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
}

.plan-chip:hover {
  background: rgba(255, 204, 0, 0.18);
}

.plan-chip--premium {
  background: var(--wn-fill);
  color: var(--wn-label);
}

.plan-chip img {
  width: 20px;
  height: 20px;
  object-fit: contain;
}

/* --- Friends --- */
.friend-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--wn-fill);
  color: var(--wn-label);
  font-size: 11px;
  font-weight: 700;
}

/* --- Controls --- */
.segmented {
  flex-shrink: 0;
  display: inline-flex;
  padding: 2px;
  border-radius: 9px;
  background: var(--wn-fill);
}

.segmented__btn {
  min-width: 64px;
  height: 28px;
  padding: 0 10px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: var(--wn-label);
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}

.segmented__btn--active {
  background: var(--wn-segment);
  font-weight: 600;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.14);
}

.select {
  flex-shrink: 0;
  max-width: 50%;
  height: 32px;
  padding: 0 10px;
  border: 0;
  border-radius: 8px;
  background: var(--wn-fill);
  color: var(--wn-label);
  font: inherit;
  font-size: 15px;
  cursor: pointer;
}

.profile__version {
  margin: 32px 0 0;
  text-align: center;
  font-size: 12px;
  color: var(--wn-tertiary);
}

@media (max-width: 520px) {
  .profile__title {
    font-size: 30px;
  }

  .segmented__btn {
    min-width: 0;
  }

  .row__value {
    display: none;
  }
}

/* --- Shared by the modals and the delete confirmation --- */
.card {
  background: var(--wn-card);
  border-radius: 14px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 38px;
  padding: 0 18px;
  border: 0;
  border-radius: 12px;
  background: var(--wn-fill);
  color: var(--wn-label);
  font: inherit;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.btn:hover:not(:disabled) {
  filter: brightness(0.96);
}

.btn:disabled {
  opacity: 0.5;
  cursor: default;
}

.btn--primary {
  background: var(--wn-accent);
  color: var(--wn-accent-ink);
}

.btn--ghost {
  background: transparent;
  color: var(--wn-accent-text);
}

.btn--danger {
  background: #ff3b30;
  color: #ffffff;
}

.confirm-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1100;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px;
  -webkit-backdrop-filter: blur(6px);
  backdrop-filter: blur(6px);
}

.confirm-dialog {
  width: min(440px, 100%);
  background: var(--wn-card);
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.confirm-dialog h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--wn-label);
}

.confirm-dialog p {
  margin: 0;
  font-size: 14px;
  color: var(--wn-secondary);
  line-height: 1.5;
}

.confirm-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 6px;
}

.spinner {
  width: 12px;
  height: 12px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: profile-spin 0.8s linear infinite;
}

@keyframes profile-spin {
  to { transform: rotate(360deg); }
}

/* ========== Pairing QR + invite modals ========== */
.profile-modal__backdrop {
  position: fixed;
  inset: 0;
  z-index: 1300;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(5, 8, 14, 0.62);
  backdrop-filter: blur(14px) saturate(120%);
  -webkit-backdrop-filter: blur(14px) saturate(120%);
}

.profile-modal {
  width: 100%;
  max-width: 540px;
  display: flex;
  flex-direction: column;
  background: var(--wn-card);
  border: 1px solid color-mix(in srgb, var(--wn-label) 14%, transparent);
  border-radius: 24px;
  box-shadow:
    0 36px 80px -28px rgba(0, 0, 0, 0.65),
    0 8px 24px -10px rgba(0, 0, 0, 0.4);
  overflow: hidden;
}

.profile-modal__header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid color-mix(in srgb, var(--wn-label) 8%, transparent);
}

.profile-modal__title {
  margin: 0;
  flex: 1;
  font-size: 17px;
  font-weight: 700;
  color: var(--wn-label);
  letter-spacing: -0.01em;
}

.profile-modal__close {
  width: 34px;
  height: 34px;
  border: 1px solid color-mix(in srgb, var(--wn-label) 10%, transparent);
  border-radius: 999px;
  background: color-mix(in srgb, var(--wn-label) 5%, transparent);
  color: var(--wn-label);
  font-size: 14px;
  cursor: pointer;
}

.profile-modal__body {
  padding: 18px 20px 22px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.profile-modal__body--qr {
  align-items: center;
}

.profile-modal__hint {
  margin: 0;
  font-size: 13px;
  color: var(--wn-secondary);
  text-align: center;
  line-height: 1.4;
}

.profile-modal__notice {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: #e08600;
  text-align: center;
  line-height: 1.4;
}

.profile-modal__qr {
  position: relative;
  width: 440px;
  height: 440px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  border-radius: 18px;
  padding: 12px;
  box-shadow: 0 18px 36px -22px rgba(0, 0, 0, 0.45);
}

.signin__qr-logo {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 32%;
  height: 32%;
  transform: translate(-50%, -50%);
  /* The app icon's own squircle is about 22% of its side; the black
     ring follows the same curve so the two corners line up. */
  border-radius: 22%;
  border: 2px solid #000;
  object-fit: cover;
  overflow: hidden;
  pointer-events: none;
}

.profile-modal__qr > img:not(.signin__qr-logo) {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.profile-modal__qr-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid color-mix(in srgb, var(--wn-label) 14%, transparent);
  border-top-color: var(--wn-label);
  border-radius: 999px;
  animation: profile-spin 0.8s linear infinite;
  margin: 12px auto;
}

.profile-modal__link {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  border: 1px solid var(--wn-separator);
  border-radius: 12px;
  background: color-mix(in srgb, var(--wn-page) 60%, transparent);
}

.profile-modal__link-url {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  font-weight: 500;
  color: var(--wn-label);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.profile-modal__link-actions {
  display: flex;
  gap: 10px;
}

.profile-modal__link-actions .btn {
  flex: 1;
  justify-content: center;
}
</style>
